/** A catalog record page's load failures — itineraries, activities, activity
 *  categories (plan finding C5: an itinerary 404 spun forever; an activity's or a
 *  category's failed refresh replaced the whole page, unsaved form included): the
 *  records core's `RecordEmptyState` for a record that does not exist or that the
 *  operator may not see, pointing back to its grid; an error with Retry for any
 *  other failure; and a notice when a refresh fails while the loaded copy (and any
 *  unsaved edits) stays on screen. While the record loads, `CatalogHeaderSkeleton`
 *  holds its header's place. */
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import { Alert, Box, Button, Skeleton, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import { RecordEmptyState } from '@/features/records/page/RecordStates';
import { missingStatus } from '@/features/records/page/recordPage';
import { activitiesHref, activityCategoriesHref, itinerariesHref } from '@/utils/gridLinks';

/** How a catalog record kind is named in its load states. */
export interface CatalogRecordKind {
  /** Lower-case noun: "itinerary". */
  noun: string;
  /** Its grid, lower-case: "itineraries" ("Back to itineraries"). */
  grid: string;
  gridHref: string;
  /** What the console role lacks on a 403, and the permission that grants it. */
  area: string;
  viewPerm: string;
}

export const ITINERARY_RECORD: CatalogRecordKind = {
  noun: 'itinerary',
  grid: 'itineraries',
  gridHref: itinerariesHref(),
  area: 'itineraries',
  viewPerm: 'console_itineraries_view',
};

export const ACTIVITY_RECORD: CatalogRecordKind = {
  noun: 'activity',
  grid: 'activities',
  gridHref: activitiesHref([]),
  area: 'activities',
  viewPerm: 'console_activities_view',
};

/** Categories are served under the activities permissions. */
export const ACTIVITY_CATEGORY_RECORD: CatalogRecordKind = {
  noun: 'activity category',
  grid: 'activity categories',
  gridHref: activityCategoriesHref(),
  area: 'activities',
  viewPerm: 'console_activities_view',
};

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

const reason = (error: unknown) => (error instanceof Error && error.message ? ` — ${error.message}` : '');

const retryAction = (onRetry: () => void) => (
  <Button color="inherit" size="small" onClick={onRetry}>
    Retry
  </Button>
);

/** The whole page when the record could not be loaded at all. */
export function CatalogLoadError({
  kind,
  id,
  error,
  onRetry,
}: {
  kind: CatalogRecordKind;
  id: number;
  error: unknown;
  onRetry: () => void;
}) {
  const missing = missingStatus(error);
  const back = (
    <Button component={RouterLink} to={kind.gridHref} startIcon={<ArrowBackIcon />} size="small">
      Back to {kind.grid}
    </Button>
  );
  if (missing === 404) {
    return (
      <RecordEmptyState icon={<SearchOffIcon />} title={`${capitalize(kind.noun)} #${id} not found`} action={back}>
        No {kind.noun} has this id — it may have been deleted, or the link carries another record’s id. Search the{' '}
        {kind.grid} grid by name instead.
      </RecordEmptyState>
    );
  }
  if (missing === 403) {
    return (
      <RecordEmptyState icon={<LockOutlinedIcon />} title={`You can’t open ${kind.noun} #${id}`} action={back}>
        Your console role does not include {kind.area}. Ask an Ops admin for the {kind.viewPerm} permission.
      </RecordEmptyState>
    );
  }
  return (
    <Alert severity="error" action={retryAction(onRetry)}>
      Couldn’t load {kind.noun} #{id}
      {reason(error)}.
    </Alert>
  );
}

/** Above the editor when a refresh failed: the page keeps the copy it has. */
export function CatalogRefreshError({
  kind,
  error,
  onRetry,
}: {
  kind: CatalogRecordKind;
  error: unknown;
  onRetry: () => void;
}) {
  return (
    <Alert severity="warning" sx={{ mb: 2 }} action={retryAction(onRetry)}>
      Could not refresh this {kind.noun}
      {reason(error)}. Showing the copy loaded earlier.
    </Alert>
  );
}

/** The header's room for the Actions menu's button and Edit's (small buttons, 31 px high). */
const ACTIONS_BUTTON_PX = 94;
const EDIT_BUTTON_PX = 72;
const BUTTON_HEIGHT_PX = 31;

/** A catalog record's header while it loads, on `PageHeader`'s geometry: the leading tile
 *  (the category's icon), the title's and the subtitle's lines, and the places of the Actions
 *  button and — for an operator who will see it — Edit, so the loaded header lands where the
 *  skeleton stood. */
export function CatalogHeaderSkeleton({ leading = false, edit }: { leading?: boolean; edit: boolean }) {
  return (
    <Box
      data-header-skeleton
      sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 2, rowGap: 1, mb: 2 }}
    >
      <Box sx={{ flex: '1 1 280px', minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
          {leading && (
            <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 32, flex: 'none', mr: 0.75 }}>
              <Skeleton variant="rounded" width={32} height={32} />
            </Box>
          )}
          <Typography variant="h5" component="div" sx={{ flex: 1, minWidth: 0 }}>
            <Skeleton width="40%" />
          </Typography>
        </Box>
        <Typography variant="body2" component="div">
          <Skeleton width="50%" />
        </Typography>
      </Box>
      <Stack direction="row" spacing={1} useFlexGap sx={{ flex: 'none' }} data-header-skeleton-actions>
        <Skeleton variant="rounded" width={ACTIONS_BUTTON_PX} height={BUTTON_HEIGHT_PX} />
        {edit && <Skeleton variant="rounded" width={EDIT_BUTTON_PX} height={BUTTON_HEIGHT_PX} />}
      </Stack>
    </Box>
  );
}
